import type { EhrPatientContext } from "./elationPatientToContext";
import type { EhrMemberMatch } from "../graphql/resolveEhrMember";

export type DemographicCompare =
  | { kind: "match" }
  | { kind: "soft"; fields: ("name" | "dob")[] } // equal after normalizing away case/space/punctuation, but raw differs
  | { kind: "mismatch"; fields: ("name" | "dob")[] }; // genuinely different

// ---------------------------------------------------------------------------
// Name helpers
// ---------------------------------------------------------------------------

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0)
    .sort();
}

function nameHardEqual(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function nameSoftEqual(a: string, b: string): boolean {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.length !== tb.length) return false;
  return ta.every((tok, i) => tok === tb[i]);
}

// ---------------------------------------------------------------------------
// DOB helpers
// ---------------------------------------------------------------------------

interface ParsedDate {
  year: number;
  month: number;
  day: number;
}

/**
 * Attempt to parse common date formats to a numeric (year,month,day) triple.
 * Handles:
 *   MM/DD/YYYY  — Elation scrape format
 *   YYYY-MM-DD  — ISO date (member dob, possibly with trailing time)
 * Returns null when the value is absent or unrecognisable.
 */
function parseDate(value: string | null | undefined): ParsedDate | null {
  if (!value) return null;
  const s = value.trim();

  // MM/DD/YYYY
  const mdyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (mdyMatch) {
    return {
      year: parseInt(mdyMatch[3], 10),
      month: parseInt(mdyMatch[1], 10),
      day: parseInt(mdyMatch[2], 10),
    };
  }

  // YYYY-MM-DD (ISO, optional trailing time/timezone)
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (isoMatch) {
    return {
      year: parseInt(isoMatch[1], 10),
      month: parseInt(isoMatch[2], 10),
      day: parseInt(isoMatch[3], 10),
    };
  }

  return null;
}

function dobEqual(a: ParsedDate, b: ParsedDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

// ---------------------------------------------------------------------------
// Main comparator
// ---------------------------------------------------------------------------

export function compareDemographics(
  ctx: EhrPatientContext,
  member: EhrMemberMatch,
): DemographicCompare {
  const scrapedName = [ctx.firstName, ctx.lastName].filter(Boolean).join(" ");
  const memberName = member.name ?? "";

  type FieldStatus = "ok" | "soft" | "mismatch";

  // --- Name comparison ---
  let nameStatus: FieldStatus;
  if (nameHardEqual(scrapedName, memberName)) {
    nameStatus = "ok";
  } else if (nameSoftEqual(scrapedName, memberName)) {
    nameStatus = "soft";
  } else {
    nameStatus = "mismatch";
  }

  // --- DOB comparison ---
  const scrapedDob = parseDate(ctx.dob);
  const memberDob = parseDate(member.dob);

  let dobStatus: FieldStatus;
  if (scrapedDob === null || memberDob === null) {
    // Cannot compare — do not flag a mismatch on missing data
    dobStatus = "ok";
  } else if (dobEqual(scrapedDob, memberDob)) {
    dobStatus = "ok";
  } else {
    dobStatus = "mismatch";
  }

  // --- Combine ---
  const mismatchFields: ("name" | "dob")[] = [];
  const softFields: ("name" | "dob")[] = [];

  if (nameStatus === "mismatch") mismatchFields.push("name");
  if (dobStatus === "mismatch") mismatchFields.push("dob");
  if (nameStatus === "soft") softFields.push("name");

  if (mismatchFields.length > 0) {
    return { kind: "mismatch", fields: mismatchFields };
  }
  if (softFields.length > 0) {
    return { kind: "soft", fields: softFields };
  }
  return { kind: "match" };
}
